import { clean, simplify } from "./format";
import type { Answer } from "./types";

const EPS = 1e-6;

/** Normalise user input: trim, unify minus signs, decimal comma → dot, collapse spaces. */
export function normalizeInput(raw: string): string {
  return raw
    .trim()
    .replace(/[−–]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/(\d),(\d)/g, "$1.$2")
    .replace(/\s*\/\s*/g, "/")
    .toLowerCase();
}

/** Parse "3/4" or "-1/2" (also "1 1/2" as a mixed number) into a fraction. */
export function parseFraction(raw: string): { num: number; den: number } | null {
  const s = normalizeInput(raw);
  const mixed = /^(-?)(\d+) (\d+)\/(\d+)$/.exec(s);
  if (mixed) {
    const sign = mixed[1] === "-" ? -1 : 1;
    const whole = Number(mixed[2]);
    const n = Number(mixed[3]);
    const d = Number(mixed[4]);
    if (d === 0) return null;
    return simplify(sign * (whole * d + n), d);
  }
  const simple = /^(-?\d+)\/(-?\d+)$/.exec(s);
  if (simple) {
    const n = Number(simple[1]);
    const d = Number(simple[2]);
    if (d === 0) return null;
    return simplify(n, d);
  }
  return null;
}

/** Parse a number, accepting decimals, fractions and mixed numbers. Returns null when not a number. */
export function parseNumber(raw: string): number | null {
  const s = normalizeInput(raw);
  if (s === "") return null;
  const f = parseFraction(s);
  if (f) return f.num / f.den;
  if (!/^-?\d*\.?\d+$/.test(s)) return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
}

function nearlyEqual(a: number, b: number, tolerance = EPS): boolean {
  return Math.abs(a - b) <= tolerance + 1e-12;
}

/** Normalise short algebraic / symbolic text: drop spaces and multiplication dots, unify minus, lowercase. */
export function normalizeText(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[−–]/g, "-")
    .replace(/[·*×]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".");
}

const TRUE_WORDS = new Set(["true", "tak", "prawda", "t", "yes", "y", "1"]);
const FALSE_WORDS = new Set(["false", "nie", "fałsz", "falsz", "n", "no", "f", "0"]);

/** Check a typed / clicked answer against the question's answer spec. */
export function checkAnswer(answer: Answer, input: string | number | boolean): boolean {
  switch (answer.mode) {
    case "choice4": {
      const idx = typeof input === "number" ? input : Number(input);
      return idx === answer.correctIndex;
    }
    case "numeric": {
      const v = typeof input === "number" ? input : parseNumber(String(input));
      if (v === null) return false;
      return nearlyEqual(clean(v), clean(answer.value), answer.tolerance ?? EPS);
    }
    case "fraction": {
      const s = String(input);
      const f = parseFraction(s);
      const target = simplify(answer.num, answer.den);
      if (f) {
        if (answer.acceptEquivalent) return f.num * target.den === f.den * target.num;
        // exact form required (but allow "3/4" for "3/4" only; simplify() already normalised sign)
        const rawParts = normalizeInput(s).split("/");
        return Number(rawParts[0]) === answer.num && Number(rawParts[1]) === answer.den;
      }
      if (!answer.acceptEquivalent) return false;
      const v = parseNumber(s);
      return v !== null && nearlyEqual(v, target.num / target.den);
    }
    case "numericSet": {
      const parts =
        typeof input === "number"
          ? [input]
          : normalizeInput(String(input))
              .split(/[;]| i | or | lub |\s+/)
              .map((p) => p.trim())
              .filter((p) => p.length > 0)
              .map(parseNumber);
      if (parts.some((p) => p === null)) return false;
      const given = (parts as number[]).map(clean).sort((a, b) => a - b);
      const wanted = [...answer.values].map(clean).sort((a, b) => a - b);
      if (given.length !== wanted.length) return false;
      return given.every((g, i) => nearlyEqual(g, wanted[i] as number));
    }
    case "text": {
      const given = normalizeText(String(input));
      if (given === "") return false;
      return [answer.value, ...(answer.accept ?? [])].some((v) => normalizeText(v) === given);
    }
    case "truefalse": {
      if (typeof input === "boolean") return input === answer.value;
      const s = normalizeInput(String(input));
      if (TRUE_WORDS.has(s)) return answer.value === true;
      if (FALSE_WORDS.has(s)) return answer.value === false;
      return false;
    }
  }
}
